import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import api from '../../services/api';
import PageTitle from '../shared/PageTitle';
import { toast } from 'react-hot-toast';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Complaints = () => {
  const { currentUser } = useContext(AuthContext);
  const location = useLocation();
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [error, setError] = useState(null);

  // For complaint details/response
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // For new complaint form - check if we should show it immediately
  const [showNewComplaintForm, setShowNewComplaintForm] = useState(
    location.state?.showNewComplaint === true
  );
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'maintenance',
    villa_id: ''
  });
  const [villas, setVillas] = useState([]);

  // For filtering
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredComplaints.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('complaints-table-section');
    }, 50);
  };

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('complaints-table-section');
      }, 50);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('complaints-table-section');
      }, 50);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Generate a unique CSS class name based on the current settings
  const themeClassName = `theme-header-${Date.now()}`;

  // Get theme colors from settings
  const bgColor = settings.block_title_bg_color || '#f8f9fa';
  const textColor = settings.block_title_color || '#8B4513';

  useEffect(() => {
    // Lock body scroll when modal is open
    if (showNewComplaintForm || showDetailsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showNewComplaintForm, showDetailsModal]);

  useEffect(() => {
    const fetchComplaints = async () => {
      try {
        setLoading(true);
        console.log('Fetching complaints for owner...');

        // Get complaints data for owner
        const [complaintsResponse, villasResponse] = await Promise.all([
          api.get('/api/complaints/owner'),
          api.get('/api/villas/owner')
        ]);

        console.log('Complaints response:', complaintsResponse.data);
        console.log('Villas response:', villasResponse.data);

        // Store owner's villas for the form
        if (Array.isArray(villasResponse.data)) {
          setVillas(villasResponse.data);

          // Set default villa_id for new complaints if owner has villas
          if (villasResponse.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              villa_id: villasResponse.data[0].id
            }));
          }
        }

        // Process complaints data
        if (Array.isArray(complaintsResponse.data)) {
          // Ensure each complaint has a responses array
          const processedComplaints = complaintsResponse.data.map(complaint => ({
            ...complaint,
            responses: complaint.responses || []
          }));

          setComplaints(processedComplaints);
          setFilteredComplaints(processedComplaints);
        } else {
          throw new Error('Invalid complaints data format');
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching complaints:', err);
        setError('Failed to load complaints. ' + (err.message || ''));

        // Provide mock data if API fails
        provideMockData();
      } finally {
        setLoading(false);
      }
    };

    // Create mock complaints data when API fails
    const provideMockData = () => {
      console.log('Using mock complaints data due to API failure');

      // Get current date for timestamps
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);

      // Mock villa data
      const mockVillas = [
        {
          id: 1,
          villa_number: 'V-22',
          owner_id: currentUser?.id
        }
      ];

      setVillas(mockVillas);

      // Set default villa_id for new complaints
      setFormData(prev => ({
        ...prev,
        villa_id: mockVillas[0].id
      }));

      // Mock complaints data with current dates
      const mockComplaints = [
        {
          id: 1,
          subject: 'Water Leakage in Bathroom',
          description: 'There is water leaking from the ceiling in the master bathroom.',
          status: 'open',
          category: 'plumbing',
          date: today.toISOString().split('T')[0],
          created_at: today.toISOString(),
          last_update: today.toISOString(),
          villa_id: 1,
          villa_number: 'V-22',
          responses: []
        },
        {
          id: 2,
          subject: 'Electrical Issue in Kitchen',
          description: 'The kitchen lights are flickering intermittently.',
          status: 'in_progress',
          category: 'electrical',
          date: yesterday.toISOString().split('T')[0],
          created_at: yesterday.toISOString(),
          last_update: today.toISOString(),
          villa_id: 1,
          villa_number: 'V-22',
          responses: [
            {
              id: 1,
              text: 'We have assigned an electrician to check your issue. Expected visit tomorrow.',
              created_by: 'Admin User',
              created_at: new Date().toISOString()
            }
          ]
        },
        {
          id: 3,
          subject: 'Gate Remote Not Working',
          description: 'The main gate remote control has stopped working.',
          status: 'resolved',
          category: 'security',
          date: lastWeek.toISOString().split('T')[0],
          created_at: lastWeek.toISOString(),
          last_update: yesterday.toISOString(),
          villa_id: 1,
          villa_number: 'V-22',
          responses: [
            {
              id: 1,
              text: 'Security team will check the remote and provide a replacement if needed.',
              created_by: 'Admin User',
              created_at: lastWeek.toISOString()
            },
            {
              id: 2,
              text: 'The remote has been replaced with a new one. Please collect it from the security office.',
              created_by: 'Admin User',
              created_at: yesterday.toISOString()
            }
          ]
        }
      ];

      setComplaints(mockComplaints);
      setFilteredComplaints(mockComplaints);
    };

    if (currentUser && currentUser.role === 'owner') {
      fetchComplaints();
    }
  }, [currentUser]);

  // Filter complaints when filter or search changes
  useEffect(() => {
    let results = [...complaints];

    // Apply status filter
    if (filterStatus !== 'all') {
      results = results.filter(complaint => complaint.status === filterStatus);
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      results = results.filter(complaint =>
        complaint.subject?.toLowerCase().includes(search) ||
        complaint.description?.toLowerCase().includes(search) ||
        complaint.category?.toLowerCase().includes(search) ||
        complaint.villa_number?.toLowerCase().includes(search)
      );
    }

    setFilteredComplaints(results);
  }, [complaints, filterStatus, searchTerm]);

  // Add validateForm function
  const validateForm = () => {
    const errors = {};
    const trimmedSubject = formData.subject.trim();
    const trimmedDescription = formData.description.trim();

    if (!trimmedSubject) {
      errors.subject = 'Subject is required';
    }

    if (!trimmedDescription) {
      errors.description = 'Description is required';
    }

    if (!formData.villa_id) {
      errors.villa_id = 'Please select a villa';
    }

    if (!formData.category) {
      errors.category = 'Please select a category';
    }

    setFormErrors(errors);
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitComplaint = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // Show first error message
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFormSubmitting(true);
    try {
      // Format data for API with trimmed values
      const apiData = {
        title: formData.subject.trim(),
        description: formData.description.trim(),
        category: formData.category,
        villa_id: parseInt(formData.villa_id)
      };

      console.log('Submitting complaint data:', apiData);

      // Call API to create new complaint
      const response = await api.post('/api/complaints', apiData);
      console.log('Complaint created:', response.data);

      // Close form and refresh list
      setShowNewComplaintForm(false);

      // Reset form
      setFormData({
        subject: '',
        description: '',
        category: 'maintenance',
        villa_id: villas.length > 0 ? villas[0].id : ''
      });

      // Clear errors
      setFormErrors({});

      // Refresh complaints list
      const complaintsResponse = await api.get('/api/complaints/owner');
      if (Array.isArray(complaintsResponse.data)) {
        const processedComplaints = complaintsResponse.data.map(complaint => ({
          ...complaint,
          responses: complaint.responses || []
        }));
        setComplaints(processedComplaints);
        setFilteredComplaints(processedComplaints);
      }

      toast.success('Complaint submitted successfully!');
    } catch (err) {
      console.error('Error creating complaint:', err);
      toast.error(err.response?.data?.message || 'Failed to submit complaint');
    } finally {
      setFormSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="badge bg-danger" style={{ lineHeight: '200%' }}>Open</span>;
      case 'in_progress':
        return <span className="badge bg-warning" style={{ lineHeight: '200%' }}>In Progress</span>;
      case 'resolved':
        return <span className="badge bg-success" style={{ lineHeight: '200%' }}>Resolved</span>;
      default:
        return <span className="badge bg-secondary" style={{ lineHeight: '200%' }}>Unknown</span>;
    }
  };

  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Add CSS styles directly to the document head
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      /* Apply direct styles to these elements */
      .card-header.${themeClassName},
      .modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
        border-color: ${bgColor} !important;
      }
      
      /* Force all children to have the text color */
      .card-header.${themeClassName} *,
      .modal-header.${themeClassName} * {
        color: ${textColor} !important;
      }
      
      /* Override any other rules */
      .card .card-header.${themeClassName},
      .modal .modal-header.${themeClassName},
      .card-header.${themeClassName},
      .modal-header.${themeClassName},
      div.card-header.${themeClassName},
      div.modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      
      /* Direct style application */
      #root .card-header.${themeClassName},
      html body .card-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      
      /* For Description section specifically */
      .card-header.${themeClassName}[class*="theme-header"] {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, [themeClassName, bgColor, textColor]);

  // Add new function to handle modal close
  const handleCloseNewComplaintForm = () => {
    setShowNewComplaintForm(false);
    setFormErrors({});
    setFormData({
      subject: '',
      description: '',
      category: 'maintenance',
      villa_id: villas.length > 0 ? villas[0].id : ''
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <PageTitle
        icon="fa-exclamation-triangle"
        title="My Complaints / Suggestions"
        action={
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            <Link to="/owner/dashboard" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => {
                setFormErrors({});
                setShowNewComplaintForm(true);
              }}
              disabled={villas.length === 0}
            >
              <i className="fas fa-plus me-2"></i> Add New
            </button>
          </div>
        }
      />
      <p className="text-muted mb-4">View and manage your complaints / suggestions</p>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {villas.length === 0 && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You need to have a villa assigned to your account before you can create complaints / suggestions.
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search complaints / suggestions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Complaints List */}
      {filteredComplaints.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          No complaints / suggestions found. {filterStatus !== 'all' && 'Try changing your filter.'}
        </div>
      ) : (
        <div className="card shadow" id="complaints-table-section">
          <div
            className={`card-header ${themeClassName}`}
            style={{
              backgroundColor: bgColor,
              color: textColor
            }}
          >
            <h5 className="mb-0">Your Complaints / Suggestions ({filteredComplaints.length})</h5>
          </div>
          <div className="list-group list-group-flush">
            {currentItems.map(complaint => (
              <div key={complaint.id} className="list-group-item">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{complaint.subject}</h5>
                    <div className="text-muted small mb-2">
                      <span className="me-3">
                        <i className="fas fa-home me-1"></i> Villa {complaint.villa_number}
                      </span>
                      <span className="me-3">
                        <i className="fas fa-tag me-1"></i> {complaint.category}
                      </span>
                      <span>
                        <i className="fas fa-calendar me-1"></i> {formatDate(complaint.date || complaint.created_at)}
                      </span>
                    </div>
                    <p className="mb-1">{complaint.description && complaint.description.length > 150
                      ? `${complaint.description.substring(0, 150)}...`
                      : complaint.description}</p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {getStatusBadge(complaint.status)}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setSelectedComplaint(complaint);
                        setShowDetailsModal(true);
                      }}
                    >
                      <i className="fas fa-eye me-1"></i> View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredComplaints.length > 0 && (
        <div className="card-footer d-flex justify-content-between align-items-center">
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredComplaints.length)} of {filteredComplaints.length} complaints / suggestions
          </div>

          <nav aria-label="Visitor pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
              </li>

              {/* Simplified pagination - show only certain page numbers */}
              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;

                // Show first page, last page, current page, and pages around current page
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(pageNumber)}>
                        {pageNumber}
                      </button>
                    </li>
                  );
                }

                // Show ellipsis
                if (
                  pageNumber === 2 && currentPage > 3 ||
                  pageNumber === totalPages - 1 && currentPage < totalPages - 2
                ) {
                  return (
                    <li key={pageNumber} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }

                return null;
              })}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* New Complaint Modal */}
      {showNewComplaintForm && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1080 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div
                  className={`modal-header ${themeClassName}`}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor
                  }}
                >
                  <h5 className="modal-title">New Complaint / Suggestion</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseNewComplaintForm}
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                    disabled={formSubmitting}
                  ></button>
                </div>
                <form onSubmit={handleSubmitComplaint}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label htmlFor="villa_id" className="form-label">Select Villa</label>
                      <select
                        className={`form-select ${formErrors.villa_id ? 'is-invalid' : ''}`}
                        id="villa_id"
                        name="villa_id"
                        value={formData.villa_id}
                        onChange={handleInputChange}
                      >
                        {villas.map(villa => (
                          <option key={villa.id} value={villa.id}>
                            {villa.villa_number}
                          </option>
                        ))}
                      </select>
                      {formErrors.villa_id && <div className="invalid-feedback">{formErrors.villa_id}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="category" className="form-label">Category</label>
                      <select
                        className={`form-select ${formErrors.category ? 'is-invalid' : ''}`}
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        <option value="maintenance">Maintenance</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="security">Security</option>
                        <option value="other">Other</option>
                      </select>
                      {formErrors.category && <div className="invalid-feedback">{formErrors.category}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="subject" className="form-label">Subject</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Enter complaint subject"
                      />
                      {formErrors.subject && <div className="invalid-feedback">{formErrors.subject}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">Description</label>
                      <textarea
                        className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                        placeholder="Describe your complaint in detail"
                      />
                      {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseNewComplaintForm}
                      disabled={formSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={formSubmitting}
                    >
                      {formSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        <>Submit</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }}></div>
        </>
      )}

      {/* Complaint Details Modal */}
      {showDetailsModal && selectedComplaint && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1080 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div
                  className={`modal-header ${themeClassName}`}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor
                  }}
                >
                  <h5 className="modal-title">Complaint / Suggestion Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedComplaint(null);
                    }}
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex justify-content-between mb-3">
                    <h5>{selectedComplaint.subject}</h5>
                    {getStatusBadge(selectedComplaint.status)}
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <p><strong>Villa:</strong> {selectedComplaint.villa_number}</p>
                      <p><strong>Category:</strong> {selectedComplaint.category}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Submitted:</strong> {formatDate(selectedComplaint.date || selectedComplaint.created_at)}</p>
                      <p><strong>Last Update:</strong> {formatDate(selectedComplaint.last_update || selectedComplaint.updated_at)}</p>
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div
                      className={`modal-header ${themeClassName}`}
                      style={{
                        backgroundColor: bgColor,
                        color: textColor
                      }}
                    >
                      <h6 className="mb-0">Description</h6>
                    </div>
                    <div className="card-body">
                      <p>{selectedComplaint.description}</p>
                    </div>
                  </div>

                  {selectedComplaint.responses && selectedComplaint.responses.length > 0 ? (
                    <div className="card mb-3">
                      <div
                        className={`modal-header ${themeClassName}`}
                        style={{
                          backgroundColor: bgColor,
                          color: textColor
                        }}
                      >
                        <h6 className="mb-0">Responses from Management</h6>
                      </div>
                      <div className="card-body p-0">
                        <div className="responses-list">
                          {selectedComplaint.responses.map((response, index) => (
                            <div key={index} className="border-bottom p-3">
                              <div className="d-flex justify-content-between">
                                <strong>{response.created_by || 'Admin'}</strong>
                                <span className="text-muted small">
                                  {formatDate(response.created_at)}
                                </span>
                              </div>
                              <p className="mb-0 mt-2">{response.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    selectedComplaint.status === 'resolved' ? (
                      <div className="alert alert-success">
                        <i className="fas fa-check-circle me-2"></i>
                        This complaint / suggestion has been resolved.
                      </div>
                    ) : selectedComplaint.status === 'in_progress' ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-clock me-2"></i>
                        This complaint / suggestion is being addressed by the management.
                      </div>
                    ) : null
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedComplaint(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }}></div>
        </>
      )}
    </div>
  );
};

export default Complaints; 